import {
  Button,
  CancelIcon,
  CheckIcon,
  Form,
  FormField,
  ModalDialog,
  Switch,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import { SharingRequest } from "../../http/types/sharingRequests";
import { Paginated, PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import Icon from "../ui/Icon";
import Table from "../ui/Table";

/** The sharing request calls of a project or calculation, already bound to it. */
export type SharingRequestsApi = {
  // the pending ones
  useRequests: (
    query: PaginationQuery,
    signal: AbortSignal,
  ) => { data?: Paginated<SharingRequest>; error?: CustomError };
  acceptRequest: (requestId: string, edit: boolean) => Promise<void>;
  rejectRequest: (requestId: string) => Promise<void>;
};

interface SharingRequestsProps {
  api: SharingRequestsApi;
  // the sharing dialog search
  searchTerm?: string;
}

interface AcceptForm {
  edit: boolean;
}

/**
 * The pending access requests of a project or calculation, for its owner (or an admin) to accept or reject: nothing
 * while there is none.
 */
const SharingRequests: FC<SharingRequestsProps> = ({ api, searchTerm }) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);
  // prefixed: the sharing dialog opens over a list, whose own filters use the unprefixed url parameters
  const filters = useListFilters({ prefix: "sharingRequests" });
  const aborter = useRef(new AbortController());
  const [requestToAccept, setRequestToAccept] = useState<SharingRequest>();
  const [requestToReject, setRequestToReject] = useState<SharingRequest>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = i18n.translation.sharingRequests;

  const { data, error } = api.useRequests({ ...filters.state, q: searchTerm }, aborter.current.signal);

  const submit = async (action: () => Promise<void>, onSuccess: () => void, message: string) => {
    setIsSubmitting(true);
    try {
      await action();
      dispatch(showSuccessNotification(i18n.translation, message));
      onSuccess();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAcceptSubmit = () => async (formData: AcceptForm) => {
    if (requestToAccept) {
      await submit(
        () => api.acceptRequest(requestToAccept.id, formData.edit),
        () => setRequestToAccept(undefined),
        t.accepted,
      );
    }
  };

  if (!data?.items.length) {
    return null;
  }

  return (
    <>
      <h4>{t.title}</h4>
      <Table<SharingRequest>
        filters={filters}
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.users.usersName}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.users.usersEmail}</TableHeadCell>
            <TableHeadCell align="left">{t.message}</TableHeadCell>
            <TableHeadCell align="left">{t.requestedAt}</TableHeadCell>
            <TableHeadCell></TableHeadCell>
          </TableRow>
        }
        items={data.items}
        renderRow={(item) => (
          <>
            <TableCell>
              {item.requester.firstName} {item.requester.lastName}
            </TableCell>
            <TableCell>{item.requester.email}</TableCell>
            <TableCell>{item.message}</TableCell>
            <TableCell>{formatTimeStamp(item.requestedAt, auth.identity?.language)}</TableCell>
          </>
        )}
        onRowClick={(item) => setRequestToAccept(item)}
        rowActions={(item) => [
          {
            label: t.accept,
            onClick: () => setRequestToAccept(item),
            disabled: isSubmitting,
            icon: <Icon glyph={CheckIcon} />,
          },
          {
            label: t.reject,
            onClick: () => setRequestToReject(item),
            disabled: isSubmitting,
            icon: <Icon glyph={CancelIcon} />,
            separatorBefore: true,
            variant: "danger",
          },
        ]}
        total={data.total}
        error={!!error}
      />

      {!!requestToAccept && (
        <ModalDialog onClose={() => setRequestToAccept(undefined)} title={t.acceptTitle}>
          <Form<AcceptForm>
            submitText={t.accept}
            disabled={isSubmitting}
            onSubmit={onAcceptSubmit}
            defaultValues={{ edit: false }}
            secondaryButton={
              <Button onClick={() => setRequestToAccept(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
          >
            {({ control }) => (
              <Controller
                name="edit"
                control={control}
                render={({ field }) => (
                  <FormField label={i18n.translation.common.writePermissions}>
                    {({ labelId }) => <Switch id={labelId} checked={field.value} onChange={field.onChange} />}
                  </FormField>
                )}
              />
            )}
          </Form>
        </ModalDialog>
      )}

      {!!requestToReject && (
        <ModalDialog
          onClose={() => setRequestToReject(undefined)}
          title={t.reject}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key={0} onClick={() => setRequestToReject(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key={1}
              onClick={() =>
                submit(
                  () => api.rejectRequest(requestToReject.id),
                  () => setRequestToReject(undefined),
                  t.rejected,
                )
              }
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {t.rejectQuestion}
        </ModalDialog>
      )}
    </>
  );
};

export default SharingRequests;
